<template>
  <div class="mail-template-page">
    <mail-template-header :disabled="pageLoading || saving || !currentTemplate"
                          :template-name="currentTemplateName"
                          @cancel="cancelChanges"
                          @save="mailSubmit"
    />

    <div v-if="formErrors.length" class="mail-alerts">
      <div v-for="(error, index) in formErrors"
           :key="`mail-error-${index}`"
           class="uk-alert-danger"
           uk-alert
      >
        <p class="mb-0">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </div>

    <default-loading v-if="pageLoading"></default-loading>

    <div v-else class="mail-template-layout">
      <mail-template-general-info class="mail-layout-info"
                                  :mail-type-options="mailTypeOptions"
                                  :selected-mail-type.sync="selectedMailType"
                                  :subject-error="fieldErrors.subject || ''"
                                  :subject.sync="currentSubject"
                                  @clear-subject-error="clearFieldError('subject')"
      />

      <mail-template-editor-card class="mail-layout-editor"
                                 :codes="currentCodes"
                                 :content-error="fieldErrors.content || ''"
                                 :editor-key="editorKey"
                                 :template="currentTemplate"
                                 :word-count="wordCount"
                                 @change="onEditorChange"
                                 @focus-preview="focusPreview"
      />

      <mail-template-preview ref="previewPane"
                             class="mail-layout-preview"
                             :auto-sent-footer="autoSentFooter"
                             :preview-html="previewHtml"
                             :preview-mode.sync="previewMode"
      />
    </div>
  </div>
</template>

<script>
import store from "@/core/services/index";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import { showSuccess, showError } from "@/utils/alertUtils";
import MailTemplateHeader from "@/view/company/Mail/components/MailTemplateHeader.vue";
import MailTemplateGeneralInfo from "@/view/company/Mail/components/MailTemplateGeneralInfo.vue";
import MailTemplateEditorCard from "@/view/company/Mail/components/MailTemplateEditorCard.vue";
import MailTemplatePreview from "@/view/company/Mail/components/MailTemplatePreview.vue";
import mail_module, {
  MODULE_NAME as MAIL_MODULE_NAME,
  ITEM as MAIL_ITEM,
  BASE_URL as MAIL_BASE_URL,
  CODES_BASE_URL as MAIL_CODES_BASE_URL,
  GET_ITEM as MAIL_GET_ITEM,
  ERROR as MAIL_ERROR,
  UPDATE_ITEM as MAIL_UPDATE_ITEM,
  GET_CODES as MAIL_GET_CODES,
  CODES as MAIL_CODES,
} from "@/core/services/store/mail_template.module";
import company_module, {
  MODULE_NAME as COMPANY_MODULE_NAME,
  ITEM as COMPANY_ITEM,
  BASE_URL as COMPANY_BASE_URL,
  GET_ITEM as COMPANY_GET_ITEM,
} from "@/core/services/store/company.module";
import { AUTH as AUTH_USER } from "@/core/services/store/auth.module";

const HIGHLIGHT_CODE_KEYS = [
  "full_name",
  "fullname",
  "name",
  "first_name",
  "last_name",
  "company_name",
  "company",
  "k.eposta",
  "keposta",
  "e.adi",
  "e.adı",
  "e_adi",
  "eadi",
  "training_name",
];

const RAW_CODE_KEYS = [
  "slink",
  "link",
  "url",
  "button",
  "login_url",
  "email_link",
];

export default {
  name: "MailTemplateEditorPage",
  components: {
    DefaultLoading,
    MailTemplateHeader,
    MailTemplateGeneralInfo,
    MailTemplateEditorCard,
    MailTemplatePreview,
  },
  data() {
    return {
      activeTemplateId: null,
      previewMode: "desktop",
      pageLoading: false,
      saving: false,
      editorKey: 0,
      formErrors: [],
      fieldErrors: {},
      snapshot: {},
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MAIL_MODULE_NAME, mail_module);
    registerStoreModule(COMPANY_MODULE_NAME, company_module);
  },
  computed: {
    templates() {
      const item = store.getters[MAIL_MODULE_NAME + "/" + MAIL_ITEM];
      return Array.isArray(item) ? item : [];
    },
    codes() {
      const codes = store.getters[MAIL_MODULE_NAME + "/" + MAIL_CODES];
      return Array.isArray(codes) ? codes : [];
    },
    company() {
      return store.getters[COMPANY_MODULE_NAME + "/" + COMPANY_ITEM] || {};
    },
    currentUser() {
      const live = store.getters.currentUser || {};
      let stored = {};
      try {
        stored = store.getters[AUTH_USER] || {};
        if (typeof stored === "string") {
          stored = JSON.parse(stored);
        }
      } catch (e) {
        stored = {};
      }
      if (!stored || typeof stored !== "object") stored = {};
      const nested =
          live.user && typeof live.user === "object" ? live.user : {};
      const storedNested =
          stored.user && typeof stored.user === "object" ? stored.user : {};
      return Object.assign({}, storedNested, stored, nested, live);
    },
    errors() {
      return store.getters[MAIL_MODULE_NAME + "/" + MAIL_ERROR];
    },
    currentTemplate() {
      return (
          this.templates.find(
              (item) => String(item.id) === String(this.activeTemplateId)
          ) || null
      );
    },
    currentTemplateName() {
      return this.templateName(this.currentTemplate);
    },
    mailTypeOptions() {
      return this.templates.map((item) => ({
        id: item.id,
        name: this.templateName(item),
      }));
    },
    selectedMailType: {
      get() {
        return (
            this.mailTypeOptions.find(
                (item) => String(item.id) === String(this.activeTemplateId)
            ) || null
        );
      },
      set(value) {
        if (value && value.id != null) {
          this.onMailTypeChange(value.id);
        }
      },
    },
    currentCodes() {
      if (!this.currentTemplate) return [];
      const found = this.codes.find(
          (item) =>
              String(item.mail_template_id) === String(this.currentTemplate.id)
      );
      const list =
          found && Array.isArray(found.data) ? found.data.slice() : [];
      if (this.isEducationReminderTemplate(this.currentTemplate)) {
        return list.filter((code) => !this.isEAdiCode(code));
      }
      if (!this.isEducationAssignedTemplate(this.currentTemplate)) {
        return list;
      }
      if (list.some((code) => this.isEAdiCode(code))) {
        return list;
      }
      const trainingIndex = list.findIndex((code) => {
        const { key, compact } = this.mailCodeParts(code);
        return key === "training_name" || compact === "trainingname";
      });
      if (trainingIndex >= 0) {
        const original = list[trainingIndex];
        list.splice(trainingIndex, 1, { ...original, code: "{e.Adi}" });
        return list;
      }
      list.push({
        code: "{e.Adi}",
        key_name: "general.training_name",
        name: "Eğitim Adı",
      });
      return list;
    },
    currentSubject: {
      get() {
        return this.currentTemplate ? this.currentTemplate.subject || "" : "";
      },
      set(value) {
        if (this.currentTemplate) {
          this.$set(this.currentTemplate, "subject", value);
        }
      },
    },
    wordCount() {
      const text = this.stripHtml(
          this.currentTemplate ? this.currentTemplate.html : ""
      );
      if (!text) return 0;
      return text.split(/\s+/).filter(Boolean).length;
    },
    autoSentFooter() {
      return this.tt(
          "mail.auto_sent_footer",
          ""
      );
    },
    previewHtml() {
      if (!this.currentTemplate) return "";
      return this.buildPreviewHtml(this.currentTemplate.html || "");
    },
  },
  methods: {
    tt(key, fallback) {
      if (this.$te && this.$te(key)) {
        const translated = this.$t(key);
        if (translated && translated !== key) return translated;
      }
      return fallback;
    },
    templateName(item) {
      if (!item) return "";
      const label = this.$t(item.key_name);
      if (item.hours === undefined || item.hours === null || item.hours === "") {
        return label;
      }
      return String(label).replace("{HOUR}", item.hours);
    },
    foldTr(value) {
      return String(value || "")
          .toLowerCase()
          .replace(/ı/g, "i")
          .replace(/ş/g, "s")
          .replace(/ğ/g, "g")
          .replace(/ü/g, "u")
          .replace(/ö/g, "o")
          .replace(/ç/g, "c");
    },
    mailCodeParts(code) {
      const raw = String((code && code.code) || "")
          .replace(/[{}]/g, "")
          .toLowerCase();
      const key = this.foldTr(raw);
      const compact = key.replace(/[._\-\s]/g, "");
      return { key, compact, raw };
    },
    isEAdiCode(code) {
      const { key, compact } = this.mailCodeParts(code);
      return key === "e.adi" || key === "e_adi" || compact === "eadi";
    },
    isEducationReminderTemplate(item) {
      if (!item) return false;
      const name = this.foldTr(this.templateName(item))
          .replace(/\s+/g, " ")
          .trim();
      const key = this.foldTr(item.key_name || "");
      return (
          name.includes("hatirlat") ||
          name.includes("reminder") ||
          /hatirlat|reminder/.test(key)
      );
    },
    isEducationAssignedTemplate(item) {
      if (!item || this.isEducationReminderTemplate(item)) return false;
      const name = this.foldTr(this.templateName(item))
          .replace(/\s+/g, " ")
          .trim();
      if (
          name.includes("egitim atandiginda") ||
          name.includes("education assigned") ||
          name.includes("training assigned")
      ) {
        return true;
      }
      const key = this.foldTr(item.key_name || "");
      return /education[_]?assigned|training[_]?assigned|assigned[_]?education|assigned[_]?training|egitim[_]?atand/.test(
          key
      );
    },
    onMailTypeChange(id) {
      this.activeTemplateId = id;
      this.fieldErrors = {};
      this.formErrors = [];
      this.editorKey += 1;
    },
    onEditorChange(value) {
      if (!this.currentTemplate) return;
      this.$set(this.currentTemplate, "html", value);
      this.clearFieldError("content");
    },
    focusPreview() {
      const pane = this.$refs.previewPane;
      if (pane && typeof pane.highlight === "function") {
        pane.highlight();
      }
    },
    stripHtml(html) {
      return String(html || "")
          .replace(/<style[\s\S]*?<\/style>/gi, " ")
          .replace(/<script[\s\S]*?<\/script>/gi, " ")
          .replace(/<[^>]+>/g, " ")
          .replace(/&nbsp;/gi, " ")
          .replace(/\s+/g, " ")
          .trim();
    },
    clearFieldError(field) {
      if (!this.fieldErrors[field]) return;
      const next = { ...this.fieldErrors };
      delete next[field];
      this.fieldErrors = next;
    },
    requiredMessage(name) {
      return this.$t("validation.required_field").replace(":NAME", name);
    },
    validate() {
      const errors = [];
      const fieldErrors = {};

      if (!this.currentTemplate) {
        errors.push(this.requiredMessage(this.tt("mail.mail_type", "Mail Türü")));
        this.formErrors = errors;
        this.fieldErrors = fieldErrors;
        return false;
      }

      if (!String(this.currentSubject || "").trim()) {
        const message = this.requiredMessage(
            this.tt("mail.subject", this.$t("general.heading"))
        );
        fieldErrors.subject = message;
        errors.push(message);
      }

      if (this.stripHtml(this.currentTemplate.html).length === 0) {
        const message = this.requiredMessage(this.tt("mail.content", "İçerik"));
        fieldErrors.content = message;
        errors.push(message);
      }

      const linkedMissing = this.templates.some((template) => {
        if (!template.is_linked) return false;
        const html = String(template.html || "");
        return !html.includes("{SLINK}") || !html.includes("<!--BUTTON-->");
      });
      if (linkedMissing) {
        errors.push(
            this.$t("validation.required_field").replace(":NAME", "{SLINK}")
        );
      }

      this.formErrors = errors;
      this.fieldErrors = fieldErrors;
      return errors.length === 0;
    },
    captureSnapshot() {
      const snapshot = {};
      this.templates.forEach((item) => {
        snapshot[item.id] = {
          html: item.html || "",
          subject: item.subject || "",
        };
      });
      this.snapshot = snapshot;
    },
    cancelChanges() {
      this.templates.forEach((item) => {
        const saved = this.snapshot[item.id];
        if (!saved) return;
        this.$set(item, "html", saved.html);
        this.$set(item, "subject", saved.subject);
      });
      this.editorKey += 1;
      this.formErrors = [];
      this.fieldErrors = {};
    },
    mailSubmit() {
      if (!this.validate()) return;

      const templates = this.templates.map((template) => ({
        ...template,
        subject:
            typeof template.subject === "string" && template.subject.trim()
                ? template.subject
                : null,
      }));

      this.saving = true;
      this.$store
          .dispatch(MAIL_MODULE_NAME + "/" + MAIL_UPDATE_ITEM, {
            url: MAIL_BASE_URL,
            contents: { data: templates },
          })
          .then(() => {
            if (this.errors) {
              showError(this.errors);
              return;
            }
            showSuccess(this.$t("general.successfully_updated"));
            if (!this.templates.length) {
              return this.loadPage();
            }
            this.captureSnapshot();
          })
          .finally(() => {
            this.saving = false;
          });
    },
    sampleValueFor(code) {
      const key = String(code.code || "")
          .replace(/[{}]/g, "")
          .toLowerCase();
      const compact = key
          .replace(/ı/g, "i")
          .replace(/ş/g, "s")
          .replace(/ğ/g, "g")
          .replace(/ü/g, "u")
          .replace(/ö/g, "o")
          .replace(/ç/g, "c")
          .replace(/[._\-\s]/g, "");
      const user = this.currentUser || {};
      const firstName = String(user.first_name || user.name || "").trim();
      const lastName = String(user.last_name || user.surname || "").trim();
      const email = String(user.email || user.mail || "").trim();
      const fullName = String(
          user.full_name ||
              user.fullname ||
              [firstName, lastName].filter(Boolean).join(" ")
      ).trim();
      const sampleFirst = firstName || "Ahmet";
      const sampleLast = lastName || "Yılmaz";
      const sampleFull = fullName || "Ahmet Yılmaz";
      const sampleEmail = email || "ahmet.yilmaz@example.com";
      const companyName = this.company.name || "Softinya";
      const trainingName = this.tt("mail.sample_training", "Örnek Eğitim");
      const samples = {
        full_name: sampleFull,
        fullname: sampleFull,
        name: sampleFirst,
        first_name: sampleFirst,
        kadi: sampleFirst,
        k_adi: sampleFirst,
        "k.adi": sampleFirst,
        "k.adı": sampleFirst,
        adi: sampleFirst,
        last_name: sampleLast,
        ksoyadi: sampleLast,
        k_soyadi: sampleLast,
        "k.soyadi": sampleLast,
        "k.soyadı": sampleLast,
        soyadi: sampleLast,
        company_name: companyName,
        company: companyName,
        email: sampleEmail,
        mail: sampleEmail,
        "k.eposta": sampleEmail,
        k_eposta: sampleEmail,
        eposta: sampleEmail,
        "e.posta": sampleEmail,
        "e-posta": sampleEmail,
        slink: "#",
        link: "#",
        url: "#",
        login_url: "#",
        training_name: trainingName,
        "e.adi": trainingName,
        "e.adı": trainingName,
        e_adi: trainingName,
        certificate_name: this.tt("mail.sample_certificate", "Sertifika"),
      };
      if (samples[key]) return samples[key];
      const compactSamples = {
        firstname: sampleFirst,
        kadi: sampleFirst,
        adi: sampleFirst,
        lastname: sampleLast,
        ksoyadi: sampleLast,
        soyadi: sampleLast,
        fullname: sampleFull,
        email: sampleEmail,
        mail: sampleEmail,
        keposta: sampleEmail,
        eposta: sampleEmail,
        eadi: trainingName,
        trainingname: trainingName,
      };
      if (compactSamples[compact]) return compactSamples[compact];
      return this.variableLabel(code);
    },
    variableLabel(code) {
      if (this.isEAdiCode(code)) {
        return this.tt("general.training_name", "Eğitim Adı");
      }
      if (code && code.key_name && this.$te && this.$te(code.key_name)) {
        return this.$t(code.key_name);
      }
      return (code && (code.name || code.code)) || "";
    },
    escapeHtml(value) {
      return String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
    },
    buildPreviewHtml(html) {
      let result = String(html || "");
      const codes = [...this.currentCodes];
      if (
          this.isEducationAssignedTemplate(this.currentTemplate) &&
          !codes.some((code) => String(code.code || "") === "{training_name}")
      ) {
        codes.push({ code: "{training_name}" });
      }
      codes.sort(
          (a, b) => String(b.code || "").length - String(a.code || "").length
      );

      codes.forEach((code) => {
        if (!code || !code.code || !result.includes(code.code)) return;
        const key = String(code.code || "")
            .replace(/[{}]/g, "")
            .toLowerCase();
        const foldedKey = this.foldTr(key);
        const sample = this.sampleValueFor(code);
        const replacement = RAW_CODE_KEYS.includes(key)
            ? sample
            : HIGHLIGHT_CODE_KEYS.includes(key) ||
              HIGHLIGHT_CODE_KEYS.includes(foldedKey)
                ? `<strong class="mail-preview-var">${this.escapeHtml(sample)}</strong>`
                : this.escapeHtml(sample);
        result = result.split(code.code).join(replacement);
      });

      const footerPattern = new RegExp(
          this.autoSentFooter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
          "gi"
      );
      result = result.replace(footerPattern, "");
      return result;
    },
    async loadPage() {
      this.pageLoading = true;
      try {
        await Promise.all([
          this.$store.dispatch(MAIL_MODULE_NAME + "/" + MAIL_GET_ITEM, {
            url: MAIL_BASE_URL,
          }),
          this.$store.dispatch(MAIL_MODULE_NAME + "/" + MAIL_GET_CODES, {
            url: MAIL_CODES_BASE_URL,
          }),
          this.$store.dispatch(COMPANY_MODULE_NAME + "/" + COMPANY_GET_ITEM, {
            url: COMPANY_BASE_URL + "/self",
          }),
        ]);
        if (
            this.templates.length &&
            !this.templates.some(
                (item) => String(item.id) === String(this.activeTemplateId)
            )
        ) {
          this.activeTemplateId = this.templates[0].id;
        }
        this.captureSnapshot();
      } finally {
        this.pageLoading = false;
      }
    },
  },
  mounted() {
    this.loadPage();
  },
};
</script>

<style scoped>
.mail-template-page {
  --mail-primary: #5b4fff;
  --mail-text: #171a2f;
  --mail-muted: #9398ad;
  --mail-border: #e8eaf3;
  --mail-input-border: #e5e8f0;
  --mail-label: #1f243d;
  color: var(--mail-text);
  overflow: visible;
}

.mail-alerts {
  margin-bottom: 16px;
}

.mail-template-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.22fr) minmax(0, 1fr);
  grid-template-areas:
    "info info"
    "editor preview";
  gap: 16px;
  align-items: start;
  overflow: visible;
}

.mail-layout-info {
  grid-area: info;
  position: relative;
  z-index: 1;
}

.mail-layout-editor {
  grid-area: editor;
  position: relative;
  z-index: 1;
}

.mail-layout-preview {
  grid-area: preview;
  position: relative;
  z-index: 1;
}

.mail-layout-info:focus-within {
  z-index: 10;
}

.mail-layout-editor:focus-within {
  z-index: 10;
}

.mail-template-layout > * {
  overflow: visible;
  min-width: 0;
}

@media (max-width: 1199px) {
  .mail-template-layout {
    grid-template-columns: 1fr;
    grid-template-areas:
      "info"
      "editor"
      "preview";
  }
}
</style>
